import React, { useState, useEffect, useRef } from 'react';
import { loadPlacesLibrary } from '../../utils/googleMapsLoader';

interface GooglePlacesAutocompleteProps {
  value?: string;
  onChange: (address: string, placeDetails?: any) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  label?: string;
  required?: boolean;
  error?: string;
  countryRestrictions?: string[];
  types?: string[];
  apiKey?: string;
  onManualEntryToggle?: (isManual: boolean) => void;
}

export const GooglePlacesAutocomplete: React.FC<GooglePlacesAutocompleteProps> = ({
  value = '',
  onChange,
  placeholder = 'Enter address',
  disabled = false,
  className = '',
  label,
  required = false,
  error,
  countryRestrictions = ['in'], // Default to India
  types = ['street_address', 'premise', 'subpremise'], // Address-like results
  apiKey,
  onManualEntryToggle
}) => {
  const [inputValue, setInputValue] = useState(value);
  const [isLoading, setIsLoading] = useState(false);
  const [googleLoaded, setGoogleLoaded] = useState(false);
  const [showManualEntry, setShowManualEntry] = useState(false);
  const [manualAddress, setManualAddress] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);
  const autocompleteElementRef = useRef<any>(null);
  const manualInputRef = useRef<HTMLTextAreaElement>(null);
  const isMountedRef = useRef(true);
  const initPromiseRef = useRef<Promise<void> | null>(null);

  useEffect(() => {
    setInputValue(value);
    if (!value && !showManualEntry) {
      setManualAddress('');
    }
  }, [value, showManualEntry]);

  useEffect(() => {
    // Track mount status
    isMountedRef.current = true;
    
    // Load Google Maps Places library and initialize PlaceAutocompleteElement
    const initAutocomplete = async () => {
      // Check if component is still mounted before proceeding
      if (!isMountedRef.current) return;
      
      try {
        setIsLoading(true);
        
        // Load Places library using importLibrary
        await loadPlacesLibrary();
        
        // Check if component is still mounted after async operation
        if (!isMountedRef.current) return;
        
        // Wait for PlaceAutocompleteElement to be available
        if (!window.google?.maps?.places?.PlaceAutocompleteElement) {
          // Fallback: if new API not available, wait a bit more
          await new Promise(resolve => setTimeout(resolve, 500));
        }
        
        // Check again if component is still mounted
        if (!isMountedRef.current) return;

        if (window.google?.maps?.places?.PlaceAutocompleteElement && containerRef.current) {
          // Clear any existing element
          if (autocompleteElementRef.current) {
            containerRef.current.innerHTML = '';
            autocompleteElementRef.current = null;
          }

          // Create PlaceAutocompleteElement (UI Kit)
          const pac = new window.google.maps.places.PlaceAutocompleteElement({
            includedRegionCodes: countryRestrictions,
            includedPrimaryTypes: types,
            // Optional: Add location bias for better results
            // locationBias: { radius: 15000, center: { lat: 28.6139, lng: 77.2090 } }, // Delhi area example
          });

          pac.id = 'place-autocomplete';
          pac.setAttribute('placeholder', placeholder);
          
          if (disabled) {
            pac.setAttribute('disabled', 'true');
          }

          // Listen for place selection
          pac.addEventListener('gmp-select', async (event: any) => {
            if (!isMountedRef.current) return;
            
            const { placePrediction } = event;
            if (placePrediction) {
              try {
                const place = placePrediction.toPlace();
                
                // Fetch only the fields we need
                await place.fetchFields({
                  fields: ['id', 'displayName', 'formattedAddress', 'addressComponents', 'location'],
                });

                // Check if component is still mounted before updating state
                if (!isMountedRef.current) return;

                // The new Place API uses addressComponents (camelCase) with longText/shortText
                // We need to pass both formats for compatibility
                const placeData = {
                  place_id: place.id,
                  formatted_address: place.formattedAddress || place.displayName || '',
                  displayName: place.displayName || '',
                  // New Place API format (camelCase)
                  addressComponents: place.addressComponents || [],
                  // Also pass as address_components for backward compatibility, but convert structure
                  address_components: place.addressComponents ? place.addressComponents.map((comp: any) => ({
                    long_name: comp.longText || comp.shortText || '',
                    short_name: comp.shortText || comp.longText || '',
                    types: comp.types || []
                  })) : [],
                  geometry: place.location ? {
                    location: {
                      lat: () => place.location.lat(),
                      lng: () => place.location.lng()
                    }
                  } : undefined,
                  // Also store as plain object for compatibility
                  coordinates: place.location ? {
                    lat: place.location.lat(),
                    lng: place.location.lng()
                  } : undefined
                };

                const addressText = place.formattedAddress || place.displayName || '';
                setInputValue(addressText);
                onChange(addressText, placeData);
              } catch (_error) {
                console.error('Error fetching place details:', error);
                if (isMountedRef.current) {
                  onChange(placePrediction.text?.text || '', undefined);
                }
              }
            }
          });

          // Append to container
          containerRef.current.appendChild(pac);
          autocompleteElementRef.current = pac;
          if (isMountedRef.current) {
            setGoogleLoaded(true);
          }
        } else {
          console.warn('PlaceAutocompleteElement not available, falling back to manual entry');
          if (isMountedRef.current) {
            setShowManualEntry(true);
          }
        }
      } catch (_error) {
        console.error('Error initializing Google Places Autocomplete:', error);
        if (isMountedRef.current) {
          setShowManualEntry(true);
        }
      } finally {
        // Only update state if component is still mounted
        if (isMountedRef.current) {
          setIsLoading(false);
        }
      }
    };

    if (!disabled && apiKey) {
      initPromiseRef.current = initAutocomplete();
    }

    // Cleanup
    return () => {
      isMountedRef.current = false;
      
      if (autocompleteElementRef.current && containerRef.current) {
        try {
          containerRef.current.innerHTML = '';
        } catch (_e) {
          // Ignore cleanup errors
        }
        autocompleteElementRef.current = null;
      }
      
      // Cancel any pending promise
      initPromiseRef.current = null;
    };
  }, [apiKey, countryRestrictions, types, placeholder, disabled]);

  const handleManualAddressChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newValue = e.target.value;
    setManualAddress(newValue);
    setInputValue(newValue);
    onChange(newValue, {
      source: 'user_entered',
      rawAddress: newValue
    });
  };

  const handleToggleManual = () => {
    const newManualState = !showManualEntry;
    setShowManualEntry(newManualState);
    if (!showManualEntry) {
      // Switching to manual - use current value or empty
      setManualAddress(inputValue || '');
      // Notify parent that manual entry is now active
      onManualEntryToggle?.(true);
    } else {
      // Switching back to autocomplete - clear manual
      setManualAddress('');
      onManualEntryToggle?.(false);
    }
  };

  return (
    <div className={`space-y-1 ${className}`}>
      {label && (
        <label className="block text-sm font-medium text-gray-700">
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}
      
      {error && (
        <p className="text-sm text-red-600 mb-1">{error}</p>
      )}

      {/* Autocomplete mode (UI Kit) */}
      {!showManualEntry && (
        <div className="space-y-2">
          <div 
            ref={containerRef} 
            className={`w-full border border-gray-300 rounded-lg px-3 py-2 bg-white ${error ? 'ring-2 ring-red-500 border-red-500' : 'focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-transparent'}`}
            style={{ minHeight: '42px' }}
          />
          {/* Show selected address if available (PlaceAutocompleteElement doesn't show it after selection) */}
          {inputValue && inputValue.length > 0 && (
            <div className="text-sm text-gray-700 bg-gray-50 px-3 py-2 rounded border border-gray-200">
              <span className="font-medium">Selected address:</span> {inputValue}
            </div>
          )}
          {isLoading && !googleLoaded && (
            <p className="text-xs text-gray-500">Loading address suggestions...</p>
          )}
          {googleLoaded && (
            <p className="text-xs text-gray-500">
              Start typing to search for addresses, or{' '}
              <button
                type="button"
                onClick={handleToggleManual}
                className="text-blue-600 hover:text-blue-700 underline cursor-pointer"
                disabled={disabled}
              >
                enter it manually
              </button>
            </p>
          )}
          {!googleLoaded && !isLoading && (
            <div className="text-xs">
              <p className="text-amber-600 mb-1">
                Address autocomplete unavailable. Please{' '}
                <button
                  type="button"
                  onClick={handleToggleManual}
                  className="text-blue-600 hover:text-blue-700 underline cursor-pointer"
                  disabled={disabled}
                >
                  enter address manually
                </button>
              </p>
            </div>
          )}
        </div>
      )}

      {/* Manual entry mode */}
      {showManualEntry && (
        <div className="space-y-2">
          <label className="block text-xs text-gray-600 mb-1">
            Type the full address (as printed on mail, delivery tags, etc.)
          </label>
          <textarea
            ref={manualInputRef}
            value={manualAddress}
            onChange={handleManualAddressChange}
            placeholder={placeholder}
            disabled={disabled}
            rows={3}
            className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed bg-white text-gray-900 ${
              error ? 'border-red-500 ring-2 ring-red-500' : 'border-gray-300'
            }`}
          />
          <div className="text-xs">
            <button
              type="button"
              onClick={handleToggleManual}
              className="text-blue-600 hover:text-blue-700 underline cursor-pointer"
              disabled={disabled}
            >
              Back to suggested addresses
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default GooglePlacesAutocomplete;
